import React, { useCallback, useEffect, useState } from 'react';

import useEffectOnce from 'hooks/use-effect-once';
import { useBranchContext } from 'state/branch';
import { useBrazeContext } from 'state/braze';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMParticleContext } from 'state/mParticle';
import { getConfigValue } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import LogRocketHelper from 'utils/logrocket';

import useOneTrustSdk from './use-one-trust-sdk';

type ConsentGroups = {
  C0001: number;
  C0002: number;
  C0003: number;
  C0004: number;
};

enum OneTrustGlobalConsentState {
  OPT_OUT = 'Opt-Out',
  OPT_IN = 'Opt-In',
}

export const OneTrustCookieBanner = () => {
  const cookieComplianceConfig = getConfigValue('oneTrust')?.cookieCompliance;
  const ONETRUST_GLOBAL_CONSENT_STATE =
    cookieComplianceConfig?.globalConsentState ??
    process.env.REACT_APP_RBI_ONETRUST_COOKIE_COMPLIANCE_GLOBAL_CONSENT_STATE;
  const ONETRUST_CONSENT_CATEGORY_MPARTICLE =
    cookieComplianceConfig?.consentCategoryMParticle ??
    process.env.REACT_APP_RBI_ONETRUST_COOKIE_COMPLIANCE_CONSENT_CATEGORY_MPARTICLE ??
    '';
  const ONETRUST_CONSENT_CATEGORY_BRAZE =
    cookieComplianceConfig?.consentCategoryBraze ??
    process.env.REACT_APP_RBI_ONETRUST_COOKIE_COMPLIANCE_CONSENT_CATEGORY_BRAZE ??
    '';
  const ONETRUST_CONSENT_CATEGORY_BRANCH =
    cookieComplianceConfig?.consentCategoryBranch ??
    process.env.REACT_APP_RBI_ONETRUST_COOKIE_COMPLIANCE_CONSENT_CATEGORY_BRANCH ??
    '';

  const enableLogRocket = useFlag(LaunchDarklyFlag.ENABLE_LOGROCKET);
  const { init: initMParticle } = useMParticleContext();
  const { initBraze } = useBrazeContext();
  const { initBranch } = useBranchContext();
  const lrInstance = LogRocketHelper.getInstance().logrocket;
  const { loaded: otSdkLoaded } = useOneTrustSdk();

  const [servicesLoaded, setServiceLoaded] = useState(false);
  const [otConsentGroups, setConsentGroups] = useState({} as ConsentGroups);

  // Fucntion to initialize 3rd party services
  const initServicesWithCookies = useCallback(() => {
    if (!window.mParticle) {
      initMParticle();
    }
    if (enableLogRocket && !lrInstance) {
      LogRocketHelper.init();
    }
    if (!window.rbiAppboy) {
      initBraze();
    }
    if (!window.branch) {
      initBranch();
    }
  }, [enableLogRocket, initBranch, initBraze, initMParticle, lrInstance]);

  // loads 3rd party services once OneTrust sdk is loaded
  useEffect(() => {
    if (otConsentGroups && otSdkLoaded && !servicesLoaded) {
      initServicesWithCookies();
    }
  }, [initServicesWithCookies, otConsentGroups, otSdkLoaded, servicesLoaded]);

  const servicesInitCheck = useCallback(() => {
    const areServicesInitialized = !!window.mParticle && !!window.branch && !!window.rbiAppboy;
    if (areServicesInitialized) {
      setServiceLoaded(true);
    }
    return areServicesInitialized;
  }, []);

  // Sets a interval to check if all 3rd party services are initialized
  useEffect(() => {
    const id = setInterval(servicesInitCheck, 1000);
    if (servicesLoaded) {
      clearInterval(id);
    }
    return () => clearInterval(id);
  }, [servicesInitCheck, servicesLoaded]);

  const getConsentGroups = (consentState: String[]) => {
    const groups: ConsentGroups = {
      C0001: 0,
      C0002: 0,
      C0003: 0,
      C0004: 0,
    };

    return Object.keys(groups).reduce((accumulator, group) => {
      if (consentState.includes(group)) {
        accumulator[group] = 1;
      }
      return accumulator;
    }, groups);
  };

  // Registers onetrust consent change handler
  useEffectOnce(() => {
    if (window?.OneTrust) {
      window.OneTrust.OnConsentChanged((event: any) => {
        const consentState = event?.detail ?? [];
        const consentGroups = getConsentGroups(consentState);

        setConsentGroups(consentGroups);
        LocalStorage.setItem(StorageKeys.ONETRUST_COOKIE_CONSENT_GROUPS, consentGroups);
      });
    }
  });

  // Switches off tracking for each service if data tracking is opted out
  useEffect(() => {
    // reads consent state for each consent category
    const mparticleConsentState = otConsentGroups[ONETRUST_CONSENT_CATEGORY_MPARTICLE];
    const brazeConsentState = otConsentGroups[ONETRUST_CONSENT_CATEGORY_BRAZE];
    const branchConsentState = otConsentGroups[ONETRUST_CONSENT_CATEGORY_BRANCH];

    // if consent state is opted out for respective service, data tracking is opted out
    if (servicesLoaded) {
      const isOptOutState = ONETRUST_GLOBAL_CONSENT_STATE === OneTrustGlobalConsentState.OPT_OUT;
      if (mparticleConsentState === 0 || isOptOutState) {
        window.mParticle.setOptOut(true);
      }
      if (brazeConsentState === 0 || isOptOutState) {
        window.rbiAppboy.stopWebTracking();
      }
      if (branchConsentState === 0 || isOptOutState) {
        window.branch.disableTracking(true);
      }
      if (mparticleConsentState === 1) {
        window.mParticle.setOptOut(false);
      }
      if (brazeConsentState === 1) {
        window.rbiAppboy.resumeWebTracking();
      }
      if (branchConsentState === 1) {
        window.branch.disableTracking(false);
      }
    }
  }, [
    ONETRUST_CONSENT_CATEGORY_BRANCH,
    ONETRUST_CONSENT_CATEGORY_BRAZE,
    ONETRUST_CONSENT_CATEGORY_MPARTICLE,
    ONETRUST_GLOBAL_CONSENT_STATE,
    otConsentGroups,
    servicesLoaded,
  ]);

  return <div data-testid="one-trust-cookie-banner" />;
};
